import { XR } from '@orderx/lab/xr';
import React, { useState } from 'react';
import { ProductType, OrderProduct } from '../../../XV2';
import { RootIdPage } from '../../../pages/id/RootIdPage';
import styled from 'styled-components';
import moment from 'moment';
import clsx from 'clsx';
import { useInterval } from '../../../hooks/interval';
import './OrderView.scss';
import { toastShowSuccess } from '@orderx/lab/xr/toast';
import { useCurrency } from '@orderx/lab/ui';
import QRCode from 'qrcode.react';
import { OrderIdPageResult } from '../../../pages/order/id/OrderIdPage';
import { BoxAnimation } from '../../box-animation/BoxAnimation';
import { useXV1 } from '../../../useXV1';
import { XV1 } from '../../../XV1';

const Wrapper = styled.div`
    .ticket {
        padding: var(--XR-gap4);
        transition: border 200ms;
        position: relative;
        font-weight: bold;

        .header {
            text-align: center;

            .location {
                color: var(--XR-color-textSecondary);
            }

            .title {
                font-size: var(--XR-fontSize2);
            }
        }

        .info {
            color: var(--XR-color-textSecondary);
            margin-top: var(--XR-gap2);

            i {
                font-size: inherit;
            }
        }

        .qr-container {
            position: relative;
            padding: var(--XR-gap4) 0;

            label {
                padding: var(--XR-gap2) var(--XR-gap4);
                position: absolute;
                top: 50%;
                left: 50%;
                transform: translate(-50%, -50%);
                background-color: white;
                color: var(--XR-color-error);
                text-align: center;
            }
        }

        .now {
            width: 64px;
            text-align: center;
        }

        &.invalid {
            border: 4px solid var(--XR-color-error);
        }
    }
`;

type Input = {
    result: OrderIdPageResult;
    onUpdate();
    linkBack?: string;
};

export function OrderView({ result, onUpdate, linkBack }: Input) {
    const products = result.products || [];
    const productTypeById = result.products.reduce((map, product) => ({ ...map, [product.id]: product.type }), {});

    return (
        <Wrapper className={'OrderView'}>
            <XR.SectionHeader
                title={`Order #${result.sequence}`}
                buttons={<XR.Button to={linkBack || [RootIdPage.route, { id: result.zone.hash }]} icon={'close'} />}
            />

            {result.orderProducts
                // TODO: HARDCODED IN FRONTEND; SHOULD COME FROM BACKEND
                .filter((orderProduct) => productTypeById[orderProduct.productId] === ProductType.TICKET)
                .map((orderProduct) => (
                    <ProductRowTicket
                        key={orderProduct.id}
                        result={result}
                        orderProduct={orderProduct}
                        onUpdate={onUpdate}
                    />
                ))}
        </Wrapper>
    );
}

function ProductRowTicket(input: { result: OrderIdPageResult; orderProduct: OrderProduct; onUpdate() }) {
    const { orderProduct, onUpdate } = input;
    const currency = useCurrency();
    const valid = !orderProduct.productDescription?.includes('ticket used');

    const invalidateTicket = useXV1(XV1.API.V2TempInvalidateTicketPost, {
        onSuccess() {
            dialog.close();
            toastShowSuccess('Ticket used');
            onUpdate();
        },
    });

    const dialog = XR.useDialog({
        toggleElement: <XR.Touchable className={'OrderView area'} variant={'text'} />,
        className: 'OrderView',
        header: { title: orderProduct.productName },
        content: <XR.Section>When marking this ticket as used, it will NOT BE VALID anymore!!!</XR.Section>,
        footer: (
            <XR.Section flexChildren gap={2}>
                <XR.Button
                    onClick={() => {
                        invalidateTicket.run({ guid: orderProduct.guid });
                    }}
                    color={'error'}
                    icon={'warning'}
                    isLoading={invalidateTicket.isLoading}>
                    Mark as used
                </XR.Button>

                <XR.Button onClick={() => dialog.close()}>Close</XR.Button>
            </XR.Section>
        ),
    });

    const text = valid ? 'Valid' : orderProduct.productDescription.split('[')[1]?.replace(']', '');

    return (
        <XR.Section>
            <BoxAnimation className={clsx('ticket', { invalid: !valid })} grid gap={2}>
                <XR.Box grid gap={2}>
                    <div className={'header'}>
                        <div className={'location'}>
                            {orderProduct.outletName || '-'} • {orderProduct.zoneName || '-'}
                        </div>

                        <div className={'title'}>{orderProduct.productName}</div>

                        <XR.Box className={'info'} align={'center'} gap={2} justify={'center'}>
                            {valid && (
                                <>
                                    <Time date={orderProduct.createdAt} />
                                    <span>•</span>
                                </>
                            )}
                            <span>{currency.format(orderProduct.priceCombined)}</span>
                        </XR.Box>
                    </div>

                    <XR.Box justify={'center'} className={'qr-container'}>
                        {!valid && <label>{text}</label>}

                        <QRCode
                            value={orderProduct.guid}
                            size={192}
                            bgColor={valid ? 'rgba(26, 197, 103, 1)' : 'rgba(239, 83, 80, 1)'}
                        />
                    </XR.Box>
                </XR.Box>

                {valid && dialog.element}
            </BoxAnimation>
        </XR.Section>
    );
}

function Time({ date }) {
    const [now, setNow] = useState(moment().format('HH:mm:ss'));
    const [fromNow, setFromNow] = useState(moment(date).fromNow());

    useInterval(
        () => {
            setNow(moment().format('HH:mm:ss'));
            setFromNow(moment(date).fromNow());
        },
        1000,
        [],
    );

    return (
        <>
            <span className={'now'}>{now}</span>
            <span>•</span>
            <span>{fromNow}</span>
        </>
    );
}
